import type {
  IFilterTicket,
  ISchemaForm,
  ISchemaFormPayload,
  ISchemaFormAssignTicket,
  ISchemaFormAssignTicketPayload,
  ISchemaFormPostAssignTicketPayload,
  ISchemaFormCloseTicket,
  ISchemaFormCloseTicketPayload,
  ISchemaFormPendingTicket,
  TaskReasonsResponse,
  ISchemaFormTransferringTicket,
  ISchemaFormTransferTicketPayload,
  IUserPayloadSchema,
  IUserSchema,
} from 'features/Ticket'
import { getPhoneFormatter } from 'helpers/common'
import { setValueLanguage } from 'helpers/language'

import type { OptionProps } from 'types'
import { ISchemaFormPendingTicketPayload } from './model'

export const filterOptionBuilder = (
  filters: IFilterTicket[] | TaskReasonsResponse[] | undefined,
): OptionProps[] => {
  if (!filters) {
    return []
  }

  const next: OptionProps[] = filters.map((filter) => {
    let name = ''

    if ('nameTh' in filter) {
      name = setValueLanguage(filter.nameTh, filter.nameEn)
    }

    if ('reasonTh' in filter) {
      name = setValueLanguage(filter.reasonTh, filter.reasonEn)
    }

    return {
      key: filter.id,
      label: name,
    }
  })

  return next
}

export const withAll = (options: OptionProps[]): OptionProps[] => {
  const next: OptionProps[] = [
    ...options,
    {
      key: '',
      label: 'common:all.label',
    },
  ]

  return next
}

export const formatCreate = (schemaForm: ISchemaForm): ISchemaFormPayload => {
  const {
    requestDate,
    statusPriority,
    type,
    taskIssue,

    products,

    branchContactCountryCode,
    contactName,
    contactPhoneNumber,
    issueDetail,
    branchDetail,
  } = schemaForm

  const {
    address,
    subDistrictEn,
    subDistrictTh,
    districtEn,
    districtTh,
    provinceEn,
    provinceTh,
    postalCode,
  } = branchDetail.branchAddress

  const contactPhoneNumberValue = getPhoneFormatter(`${contactPhoneNumber}`)
  const next: ISchemaFormPayload = {
    requestDate,
    statusPriority,
    type: Number(type),
    taskIssue: Number(taskIssue),

    ...branchDetail,
    products,

    branchContactCountryCode,
    contactName: contactName.trim(),
    ...(contactPhoneNumber &&
      typeof contactPhoneNumberValue !== undefined && {
        contactPhoneNumber: contactPhoneNumberValue,
      }),
    issueDetail: issueDetail.trim(),

    branchAddress: {
      address,
      countryEn: 'Thailand',
      countryTh: 'ไทย',
      subDistrictEn,
      subDistrictTh,
      districtEn,
      districtTh,
      provinceEn,
      provinceTh,
      postalCode,
    },
    branchContactPhoneNumber: getPhoneFormatter(
      `${branchDetail.branchContactPhoneNumber}`,
    ),
  }
  return next
}

export const formatAssignTicket = (
  value: ISchemaFormAssignTicket,
): ISchemaFormAssignTicketPayload => {
  const {
    assignDate,
    assignStart,
    assignEnd,
    users,
    issueDetail,
    statusPriority,
  } = value

  const setAssignDateTime = (date: Date): Date => {
    return new Date(
      new Date(assignDate).setHours(
        new Date(date).getHours(),
        new Date(date).getMinutes(),
        0,
        0,
      ),
    )
  }

  const start = setAssignDateTime(assignStart)
  const end = setAssignDateTime(assignEnd)

  return {
    assignStart: start,
    assignEnd: end,
    users: users.map((item) => {
      return {
        id: item.key,
        fullName: item.label,
      }
    }),
    issueDetail,
    statusPriority,
  }
}

export const formatPostTaskAssign = (
  taskId: string,
  value: ISchemaFormAssignTicket,
): ISchemaFormPostAssignTicketPayload => {
  const {
    assignDate,
    assignStart,
    assignEnd,
    users,
    issueDetail,
    statusPriority,
  } = value

  const setAssignDateTime = (date: Date): Date => {
    return new Date(
      new Date(assignDate).setHours(
        new Date(date).getHours(),
        new Date(date).getMinutes(),
        0,
        0,
      ),
    )
  }

  const start = setAssignDateTime(assignStart)
  const end = setAssignDateTime(assignEnd)

  return {
    taskId,
    assignStart: start.toISOString(),
    assignEnd: end.toISOString(),
    users: users.map((item) => {
      return {
        id: item.key,
        fullName: item.label,
      }
    }),
    issueDetail,
    statusPriority,
  }
}

export const formatTransferringTicket = (
  taskId: string,
  userId: string,
  userName: string,
  value: ISchemaFormTransferringTicket,
  transferringAt: string,
): ISchemaFormTransferTicketPayload => {
  const {
    assignEnd,
    assignStart,
    issueDetail,
    statusPriority,
    assignDate,
    transferringDetail,
    transferringReasons,
    users,
  } = value

  const setAssignDateTime = (date: Date): Date => {
    return new Date(
      new Date(assignDate).setHours(
        new Date(date).getHours(),
        new Date(date).getMinutes(),
        0,
        0,
      ),
    )
  }

  const start = setAssignDateTime(assignStart)
  const end = setAssignDateTime(assignEnd)

  return {
    taskId,
    transferringById: userId,
    transferringByUser: userName,
    transferringAt,
    assignStart: start.toISOString(),
    assignEnd: end.toISOString(),
    users: users.map((item) => {
      return {
        id: item.key,
        fullName: item.label,
      }
    }),
    issueDetail,
    statusPriority,
    transferringDetail,
    transferringReasons,
  }
}

export const formatCloseTicket = (
  value: ISchemaFormCloseTicket,
): ISchemaFormCloseTicketPayload => {
  const { completedTaskIssue, completedDetail, completedPhoto } = value

  return {
    completedTaskIssue: Number(completedTaskIssue),
    completedDetail,
    completedPhoto,
  }
}

export const formatPendingTicket = (
  value: ISchemaFormPendingTicket,
  userId: string,
  userFullName: string,
): ISchemaFormPendingTicketPayload => {
  const { pendingDetail, pendingReason } = value

  const pendingAt = new Date().toISOString() as string

  return {
    userId,
    userFullName,
    pendingAt,
    pendingReason,
    pendingDetail,
  }
}

export const formatUsers = (users: IUserPayloadSchema[]): IUserSchema[] => {
  return users?.map((value) => {
    return { key: value.id, label: value.fullName, value: value.id }
  })
}
